// ── Floor Plan Types ────────────────────────────────────────────────────────

export type TableStatus = 'available' | 'occupied' | 'reserved' | 'inactive';

/**
 * `booth`, `sofa` and `hightop` arrived with Floor Studio. The first three are the
 * original vocabulary and are kept spelled exactly as they were — renaming `circle` to
 * `round` would have rewritten every existing row for cosmetics.
 */
export type TableShape = 'circle' | 'rectangle' | 'square' | 'booth' | 'sofa' | 'hightop';

/** Shapes seated by a continuous bench rather than individual chairs. */
export const BENCH_SHAPES: readonly TableShape[] = ['booth', 'sofa'];

export interface FloorPlanTable {
  id: string;
  restaurant_id: string;
  table_number: string;
  label: string | null;
  seats: number;
  status: TableStatus;
  /**
   * @deprecated Percent-of-viewport coordinates from the old canvas. Still written for
   * one release so it keeps rendering; read `x_cm` / `y_cm` instead. See
   * docs/floor-studio-redesign.md.
   */
  position_x: number;
  /** @deprecated See {@link FloorPlanTable.position_x}. */
  position_y: number;
  shape: TableShape;
  /** @deprecated Stored in a pixel unit that was never rendered. Read `width_cm`. */
  width: number;
  /** @deprecated See {@link FloorPlanTable.width}. */
  height: number;
  rotation: number;
  zone: string | null;
  /** First-class category this table belongs to (reservations redesign). */
  area_id: string | null;
  /** Which level of the venue this table stands on. Null until the venue has a floor. */
  floor_id: string | null;
  /** Centre point on the floor, in centimetres from the floor's top-left. */
  x_cm: number | null;
  y_cm: number | null;
  /** The table top's own extent in centimetres — seating is derived, never stored. */
  width_cm: number | null;
  height_cm: number | null;
  qr_code: string | null;
  current_session_id: string | null;
  /** Active merge-group id, or null if the table is on its own */
  table_group_id: string | null;
  created_at: string;
  updated_at: string;
}

// ── Floors and the things standing on them ──────────────────────────────────

export interface VenueFloor {
  id: string;
  venue_id: string;
  name: string;
  sort_order: number;
  /** The room's extent in centimetres. Fit-to-viewport and the scale bar come from this. */
  width_cm: number;
  height_cm: number;
  /** Guests only ever see a published floor — an owner can rearrange during service. */
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export type FloorObjectKind = 'fixture' | 'wall' | 'zone' | 'label';

/** Landmarks. Not furniture — these are how a person orients in an unfamiliar room. */
export const FIXTURE_SUBTYPES = [
  'bar', 'stage', 'dj', 'host', 'restroom', 'pillar', 'planter', 'dancefloor', 'pool',
] as const;

export const WALL_SUBTYPES = ['solid', 'window', 'door', 'railing'] as const;

export type FixtureSubtype = (typeof FIXTURE_SUBTYPES)[number];
export type WallSubtype = (typeof WALL_SUBTYPES)[number];

export interface FloorObject {
  id: string;
  venue_id: string;
  floor_id: string;
  kind: FloorObjectKind;
  /** A member of FIXTURE_SUBTYPES / WALL_SUBTYPES, or 'rect' for a zone, 'text' for a label. */
  subtype: string;
  label: string | null;
  /** Centre point and extent in centimetres — the same space as the tables. */
  x_cm: number;
  y_cm: number;
  width_cm: number;
  height_cm: number;
  rotation: number;
  z_index: number;
  /** Zones only: the area every table standing inside this rectangle belongs to. */
  area_id: string | null;
  color: string | null;
  created_at: string;
  updated_at: string;
}

// ── Geometry ────────────────────────────────────────────────────────────────

/**
 * The one place chair placement is decided.
 *
 * Chairs are NEVER stored. `seats` + `shape` + extent is enough to place every one of
 * them, and deriving them is what stops the drawn seat count drifting away from the
 * number the reservation engine checks capacity against. A stored chair would be a
 * second source of truth for "how many people fit here", and the two would disagree
 * within a week.
 *
 * Everything below is centimetres in floor space, so the dashboard editor, the waiter
 * floor and the guest booking plan all draw the identical room from the identical maths.
 */

/** A dining chair, roughly. Real ones are 45–50 cm across. */
export const CHAIR_WIDTH_CM = 45;
export const CHAIR_DEPTH_CM = 42;
/** Clearance between the table edge and the front of the chair. */
export const CHAIR_GAP_CM = 10;
/**
 * Centre-to-centre spacing of place settings along a table edge.
 *
 * 60 cm is the restaurant standard for one cover, and it is what makes the arithmetic
 * agree with how tables are actually sold: a 180 cm table seats three a side, so it is a
 * six-top. At 65 the same table came out as a four-top, which would have quietly cost a
 * venue two covers on every long table it owns.
 */
export const SEAT_PITCH_CM = 60;
/** Depth of a banquette / sofa cushion. */
export const BENCH_DEPTH_CM = 55;

export interface SeatPlacement {
  /** Chair centre, centimetres in floor space. */
  x: number;
  y: number;
  /** Degrees. 0 means the chair's back faces "up" the floor (towards -y). */
  rotation: number;
}

export interface BenchPlacement {
  x: number;
  y: number;
  width_cm: number;
  depth_cm: number;
  rotation: number;
  /** One cushion per seat. */
  cushions: number;
}

export type SeatingPlan =
  | { style: 'chairs'; chairs: SeatPlacement[]; bench: null }
  | { style: 'bench'; chairs: SeatPlacement[]; bench: BenchPlacement };

/** Table-top extent for a shape, in centimetres, when the owner hasn't resized it. */
export function defaultExtentForShape(shape: TableShape): { width_cm: number; height_cm: number } {
  switch (shape) {
    case 'circle':  return { width_cm: 110, height_cm: 110 };
    case 'square':  return { width_cm: 100, height_cm: 100 };
    case 'booth':   return { width_cm: 200, height_cm: 95 };
    case 'sofa':    return { width_cm: 220, height_cm: 85 };
    case 'hightop': return { width_cm: 70,  height_cm: 70 };
    default:        return { width_cm: 180, height_cm: 90 };  // rectangle
  }
}

/** Sensible seat count for a freshly added table of this shape. */
export function defaultSeatsForShape(shape: TableShape): number {
  switch (shape) {
    case 'hightop': return 2;
    case 'rectangle':
    case 'booth':   return 6;
    case 'sofa':    return 6;
    default:        return 4;
  }
}

function rotatePoint(x: number, y: number, cx: number, cy: number, degrees: number) {
  if (!degrees) return { x, y };
  const r = (degrees * Math.PI) / 180;
  const cos = Math.cos(r), sin = Math.sin(r);
  const dx = x - cx, dy = y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** Geometry a seating plan needs, independent of where the row came from. */
export interface SeatableTable {
  shape: TableShape;
  seats: number;
  x_cm: number;
  y_cm: number;
  width_cm: number;
  height_cm: number;
  rotation?: number;
}

/**
 * Chairs evenly around a circle, starting at the top and going clockwise.
 * The chair's back faces outwards, which is why the rotation is the angle plus 90°.
 */
function chairsAroundCircle(t: SeatableTable): SeatPlacement[] {
  const out: SeatPlacement[] = [];
  const n = Math.max(0, Math.floor(t.seats));
  if (n === 0) return out;
  const radius = t.width_cm / 2 + CHAIR_GAP_CM + CHAIR_DEPTH_CM / 2;
  for (let i = 0; i < n; i++) {
    const deg = -90 + (i * 360) / n;
    const rad = (deg * Math.PI) / 180;
    out.push({
      x: t.x_cm + Math.cos(rad) * radius,
      y: t.y_cm + Math.sin(rad) * radius,
      rotation: deg + 90,
    });
  }
  return out;
}

/**
 * Chairs along the edges of a rectangle, long sides filled first.
 *
 * Long sides first is not an arbitrary preference — it is how a table is actually laid.
 * Six people at a 180 cm table sit three and three, not two-two-one-one, and a plan that
 * draws it the other way reads as wrong to anyone who has worked a floor.
 *
 * Once both long sides are full the ends take the overflow; past that, extra seats are
 * dropped rather than stacked on top of each other, because a table that cannot seat the
 * number typed into it is a data problem to surface, not a drawing to fake.
 */
function chairsAroundRect(t: SeatableTable): SeatPlacement[] {
  const n = Math.max(0, Math.floor(t.seats));
  if (n === 0) return [];

  const capLong = Math.max(1, Math.floor(t.width_cm / SEAT_PITCH_CM));
  const capShort = Math.max(1, Math.floor(t.height_cm / SEAT_PITCH_CM));
  const cap = { top: capLong, bottom: capLong, left: capShort, right: capShort };
  const count = { top: 0, bottom: 0, left: 0, right: 0 };

  // Long sides first, alternating so the two sides stay balanced, and only then the
  // ends. Round-robining all four sides instead would seat six at a 180 cm table as
  // two-two-one-one, which is not how anyone lays a table.
  let placed = 0;
  const pairs = [['top', 'bottom'], ['left', 'right']] as const;
  for (const pair of pairs) {
    for (let step = 0; placed < n; step++) {
      const side = pair[step % 2]!;
      if (count[side] < cap[side]) { count[side]++; placed++; continue; }
      // Both sides of this pair are full — move on to the ends, or give up.
      if (count[pair[0]] >= cap[pair[0]] && count[pair[1]] >= cap[pair[1]]) break;
    }
    if (placed >= n) break;
  }

  const offsetY = t.height_cm / 2 + CHAIR_GAP_CM + CHAIR_DEPTH_CM / 2;
  const offsetX = t.width_cm / 2 + CHAIR_GAP_CM + CHAIR_DEPTH_CM / 2;
  const out: SeatPlacement[] = [];

  (['top', 'bottom'] as const).forEach(side => {
    const n2 = count[side];
    for (let i = 0; i < n2; i++) {
      out.push({
        x: t.x_cm - t.width_cm / 2 + (t.width_cm * (i + 0.5)) / n2,
        y: side === 'top' ? t.y_cm - offsetY : t.y_cm + offsetY,
        rotation: side === 'top' ? 0 : 180,
      });
    }
  });
  (['left', 'right'] as const).forEach(side => {
    const n2 = count[side];
    for (let i = 0; i < n2; i++) {
      out.push({
        x: side === 'left' ? t.x_cm - offsetX : t.x_cm + offsetX,
        y: t.y_cm - t.height_cm / 2 + (t.height_cm * (i + 0.5)) / n2,
        rotation: side === 'left' ? 270 : 90,
      });
    }
  });
  return out;
}

/**
 * Where every seat at this table goes.
 *
 * A sofa is one bench with a cushion per seat. A booth is a bench on one long side and
 * chairs facing it across the table, which is what a booth is. Everything else gets
 * chairs. The table's own `rotation` is applied last, to seats and bench alike, so a
 * table turned 30° keeps its chairs.
 */
export function seatingPlan(table: SeatableTable): SeatingPlan {
  const rot = table.rotation ?? 0;
  const spin = <T extends { x: number; y: number; rotation: number }>(p: T): T => {
    const moved = rotatePoint(p.x, p.y, table.x_cm, table.y_cm, rot);
    return { ...p, x: moved.x, y: moved.y, rotation: p.rotation + rot };
  };

  if (table.shape === 'sofa' || table.shape === 'booth') {
    const seats = Math.max(0, Math.floor(table.seats));
    // A booth seats roughly half its party on the bench; a sofa seats all of them.
    const onBench = table.shape === 'sofa' ? seats : Math.ceil(seats / 2);
    const bench: BenchPlacement = spin({
      x: table.x_cm,
      y: table.y_cm - table.height_cm / 2 - BENCH_DEPTH_CM / 2,
      width_cm: table.width_cm + 20,
      depth_cm: BENCH_DEPTH_CM,
      rotation: 0,
      cushions: Math.max(1, onBench),
    });

    const facing = seats - onBench;
    const chairs: SeatPlacement[] = [];
    const offsetY = table.height_cm / 2 + CHAIR_GAP_CM + CHAIR_DEPTH_CM / 2;
    for (let i = 0; i < facing; i++) {
      chairs.push(spin({
        x: table.x_cm - table.width_cm / 2 + (table.width_cm * (i + 0.5)) / facing,
        y: table.y_cm + offsetY,
        rotation: 180,
      }));
    }
    return { style: 'bench', bench, chairs };
  }

  const round = table.shape === 'circle' || table.shape === 'hightop';
  const chairs = (round ? chairsAroundCircle(table) : chairsAroundRect(table)).map(spin);
  return { style: 'chairs', chairs, bench: null };
}

/**
 * The box a table really occupies — its top plus everything seated at it.
 *
 * Used for fit-to-content, for the "first free spot" search when a table is added, and
 * for overlap warnings. A table's own extent is not enough: two 110 cm rounds 120 cm
 * apart do not overlap, but the people sitting at them are in each other's backs.
 */
export function tableFootprint(table: SeatableTable): { x: number; y: number; width: number; height: number } {
  const plan = seatingPlan(table);
  const half = Math.max(CHAIR_WIDTH_CM, CHAIR_DEPTH_CM) / 2;
  let minX = table.x_cm - table.width_cm / 2;
  let maxX = table.x_cm + table.width_cm / 2;
  let minY = table.y_cm - table.height_cm / 2;
  let maxY = table.y_cm + table.height_cm / 2;

  const swallow = (x: number, y: number, r: number) => {
    minX = Math.min(minX, x - r); maxX = Math.max(maxX, x + r);
    minY = Math.min(minY, y - r); maxY = Math.max(maxY, y + r);
  };
  plan.chairs.forEach(c => swallow(c.x, c.y, half));
  if (plan.bench) swallow(plan.bench.x, plan.bench.y, Math.max(plan.bench.width_cm, plan.bench.depth_cm) / 2);

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Which area a point on the floor belongs to.
 *
 * This is what makes areas a property of the room rather than a dropdown: drag a table
 * into the VIP rectangle and it becomes a VIP table, with the price and the guest-facing
 * label that go with it. Later zones win, so an owner who draws a small zone on top of a
 * big one gets the specific answer rather than the general one — which is what drawing
 * one thing on top of another means everywhere else.
 */
export function areaAtPoint(zones: readonly FloorObject[], x: number, y: number): string | null {
  let found: string | null = null;
  for (const z of zones) {
    if (z.kind !== 'zone' || !z.area_id) continue;
    const inside = Math.abs(x - z.x_cm) <= z.width_cm / 2 && Math.abs(y - z.y_cm) <= z.height_cm / 2;
    if (inside) found = z.area_id;
  }
  return found;
}

/** Default extent of a brand-new floor, and the editor's snap step. */
export const DEFAULT_FLOOR_WIDTH_CM = 1600;
export const DEFAULT_FLOOR_HEIGHT_CM = 1200;
export const FLOOR_SNAP_CM = 25;

export interface FloorPlanStats {
  total: number;
  available: number;
  occupied: number;
  reserved: number;
  inactive: number;
  totalSeats: number;
}

// ── Table Groups (merged tables) ────────────────────────────────────────────

export interface TableGroupMember {
  table_id: string;
  table_number: string;
  label: string | null;
  joined_at: string;
}

export interface TableGroup {
  id: string;
  restaurant_id: string;
  status: 'active' | 'closed';
  lead_table_id: string;
  label: string | null;
  members: TableGroupMember[];
  created_by_staff_id: string | null;
  created_at: string;
  closed_at: string | null;
}

export interface MergeTablesParams {
  table_ids: string[];
  lead_table_id: string;
  label?: string | null;
}

export interface UnmergeTablesParams {
  /** Optional map of orderId → tableId for orders that need a specific home table after unmerge.
   *  Orders not in the map keep their existing table_id. */
  reassignments?: Record<string, string>;
}

export interface CreateTableParams {
  table_number: string;
  label?: string | null;
  seats?: number;
  shape?: TableShape;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  zone?: string | null;
  area_id?: string | null;
}

export interface UpdateTableParams {
  table_number?: string;
  label?: string | null;
  seats?: number;
  status?: TableStatus;
  shape?: TableShape;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  rotation?: number;
  zone?: string | null;
  area_id?: string | null;
}

// ─── Table ordering ──────────────────────────────────────────────────────────

/**
 * The canonical order for table numbers, everywhere.
 *
 * Tables were rendered in FOUR different orders, so the same 20 tables appeared
 * differently on the dashboard grid, the waiter floor and the assignment modal:
 *   - `floorPlan.repository#listTables`      → `.order('created_at')`  (creation order)
 *   - `kds.repository#listTablesWithOccupancy` → `.order('table_number')` (LEXICAL: "T-10" < "T-2")
 *   - `TableGridView`                        → strip non-digits, then localeCompare
 *   - `TableAssignmentModal`                 → localeCompare({ numeric: true })
 *
 * Postgres can't natural-sort free text, so ordering in SQL can never be right here —
 * every list sorts in JS, through this function.
 *
 * `numeric: true` is what makes "T-2" precede "T-10" instead of following it, and it
 * places pure-alpha names ("Bar", "Patio A") after numerics because digits collate
 * before letters. Note the previous `TableGridView` approach did the opposite: it
 * stripped non-digits, so "Bar" became 0 and sorted ahead of table 1, and "T1" landed
 * before "2" — it was the least correct of the four, despite being the most bespoke.
 *
 * `sensitivity: 'base'` keeps "t1"/"T1" adjacent; table numbers are already unique
 * case-insensitively at the API layer.
 *
 * Renaming a table now moves it to its correct slot everywhere. That IS the fix, not a
 * new bug.
 */
export function compareTableNumbers(a: string, b: string): number {
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
}

/** `compareTableNumbers` lifted to any record carrying a `table_number`. */
export function byTableNumber<T extends { table_number: string }>(a: T, b: T): number {
  return compareTableNumbers(a.table_number, b.table_number);
}

/**
 * What a table is CALLED on screen.
 *
 * Its own label if the owner gave it one, otherwise the table number exactly as they
 * typed it — no prefix. Eight call sites used to build `` `T-${tableNumber}` `` by hand,
 * so a table named "T1" was displayed as "T-T1" and one named "Bar" as "T-Bar". The
 * owner names their tables; the app repeats the name back, it does not decorate it.
 */
export function tableDisplayName(table: { label?: string | null; tableNumber?: string | null; table_number?: string | null }): string {
  return table.label ?? table.tableNumber ?? table.table_number ?? '';
}

/**
 * The names for a run of tables added in one go.
 *
 * A venue setting up its floor has twenty tables, not one, and adding them one dialog at
 * a time is the kind of chore that makes an owner give up halfway. Given a starting name
 * and a count this produces the sequence an owner would have typed themselves.
 *
 * Rules, in the order they matter:
 *  - Trailing digits increment, and their WIDTH is preserved: "T01" continues "T02",
 *    not "T2" — zero padding is a deliberate choice about how names sort.
 *  - A name with no trailing digits keeps its exact spelling for the first table and
 *    gains a number from the second ("Bar", "Bar2"). Renaming the owner's first table
 *    to "Bar1" when they asked for "Bar" would be presumptuous.
 *  - Names already on the floor are SKIPPED, not duplicated or failed on. Adding five
 *    more tables to a floor that already has T1–T3 should give T4–T8, which is what the
 *    owner meant. Table numbers are unique per venue, so a collision is a server error.
 *  - Comparison is case-insensitive, matching the uniqueness rule at the API.
 */
export function generateTableNames(start: string, count: number, taken: readonly string[] = []): string[] {
  const base = start.trim();
  if (!base || count < 1) return [];

  const used = new Set(taken.map(t => t.trim().toLowerCase()));
  const match = /^(.*?)(\d+)$/.exec(base);
  const prefix = match ? match[1]! : base;
  const width = match ? match[2]!.length : 0;
  let n = match ? parseInt(match[2]!, 10) : 0;

  const names: string[] = [];
  // Bounded so a bad prefix can never spin forever; every iteration either takes a name
  // or steps past one that is taken.
  for (let guard = 0; names.length < count && guard < count + used.size + 1000; guard++) {
    const name = match ? `${prefix}${String(n).padStart(width, '0')}` : (n === 0 ? base : `${base}${n + 1}`);
    if (!used.has(name.toLowerCase())) {
      names.push(name);
      used.add(name.toLowerCase());
    }
    n++;
  }
  return names;
}

// ── The public floor (what a guest is allowed to see) ───────────────────────

/**
 * A table on a published floor, as served to the guest app.
 *
 * Note what is NOT here: `status`. `occupied` / `reserved` / `inactive` is operational
 * data — it says what is happening in the room right now, not whether a guest can book
 * this table for 9pm on Saturday. Only the reservation engine can answer that, and it
 * already does, through the availability endpoint. The guest app crosses the two: a
 * table is free if the availability result names it, and taken otherwise.
 *
 * See docs/floor-studio-redesign.md D7.
 */
export interface PublicFloorTable {
  id: string;
  table_number: string;
  label: string | null;
  seats: number;
  shape: TableShape;
  x_cm: number;
  y_cm: number;
  width_cm: number;
  height_cm: number;
  rotation: number;
  area_id: string | null;
}

/** A fixture, wall, zone or label on a published floor. */
export interface PublicFloorObject {
  id: string;
  kind: FloorObjectKind;
  subtype: string;
  label: string | null;
  x_cm: number;
  y_cm: number;
  width_cm: number;
  height_cm: number;
  rotation: number;
  z_index: number;
  area_id: string | null;
  color: string | null;
}

export interface PublicFloor {
  id: string;
  name: string;
  width_cm: number;
  height_cm: number;
  objects: PublicFloorObject[];
  tables: PublicFloorTable[];
}
